import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { debounceTime, distinctUntilChanged, startWith, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { formatDecimalWithCommas, formatStat, formatStatOneDecimal, oneDecimalWithCommas } from '../../utils/format.utils';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'lib-waterfall-stack-chart',
  imports: [CapitalCasePipe],
  templateUrl: './waterfall-stack-chart.component.html',
  styleUrl: './waterfall-stack-chart.component.scss',
})
export class WaterfallStackChartComponent implements AfterViewInit, OnDestroy {
  get resolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }
  @Input() data!: any;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: any;
  protected myChart!: echarts.ECharts;
  protected subscription!: Subscription;
  protected unSubscribe$: Subject<void> = new Subject();

  protected resizeHandler = () => this.myChart?.resize();

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService
  ) { }

  ngAfterViewInit(): void {
    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        startWith(this.globalFilterService.getCurrentFilters()),
         // small delay to batch rapid updates
        switchMap((filters) => this.loadAndRenderChart(filters))
      )
      .subscribe({
        next: (res: any) => {
          this.renderChart(res);
          this.addResizeListener();
        },
        error: () => {
          this.myChart.showLoading();
        },
      });

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): any {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();
    return this.configService.getConfig(this.data.apiUrl, this.data, filters)
      .pipe(takeUntil(this.unSubscribe$))
  }

  protected renderChart(chartData?: { category: string[]; series: any[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const apiResponse: any = chartData;

    const categories = [
      'PID',
      'TO',
      'EBU Disc',
      'CES DN',
      'CES Disc',
      'EBU + CES Imp',
      'DBU Cost',
      'DBU Mar',
      'DBU Imp',
      'DBU Out',
    ];

    // map category labels to series keys
    const labelToSeriesKey: Record<string, string> = {
      'TO': 'aco',
      'EBU Disc': 'ebu_disc',
      'CES DN': 'ces_dn',
      'CES Disc': 'ces_disc',
      'EBU + CES Imp': 'ebu_ces_imp',
      'DBU Mar': 'dbu_mar',
      'DBU Imp': 'dbu_imp',
    };

    let current = 0;
    const waterfallData: any[] = [
      ['barLabel', 'barStartValue', 'barEndValue', 'referenceDatapoint'],
    ];

    for (const cat of categories) {
      switch (cat) {
        case 'PID': {
          const pidValue = apiResponse?.base?.pid;
          waterfallData.push([cat, 0, pidValue, 2]);
          current = pidValue;
          break;
        }
        case 'DBU Cost': {
          const dbuBase = apiResponse?.base.dbu_prices?.dbu_current_price;
          const dbuDelta =
            apiResponse.base.dbu_prices.dbu_proposed_price - dbuBase;

          // stacked bars (reference base + delta)
          waterfallData.push([cat, 0, dbuBase, 2]);
          waterfallData.push([cat, dbuBase, dbuBase + dbuDelta, 1]);
          current = dbuBase + dbuDelta;
          break;
        }

        case 'DBU Out': {
          const finalBase = apiResponse?.base.final_prices?.current_price;
          const finalDelta =
            apiResponse.base.final_prices.proposed_price - finalBase;

          // stacked bars
          waterfallData.push(['DBU Out', 0, finalBase, 2]);
          waterfallData.push([
            'DBU Out',
            finalBase,
            finalBase + finalDelta,
            1,
          ]);
          break;
        }

        default: {
          const key = labelToSeriesKey[cat];
          const value = apiResponse?.series[key] || 0;
          const next = current + value;
          waterfallData.push([cat, current, next, 0]);
          current = next;
        }
      }
    }

    // Responsive font scale — shrinks on tablets (0.85), scales up on TV/4K (> 1)
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.max(1, Math.round(px * fs));

    const dbuTotal = apiResponse.base.dbu_prices.dbu_proposed_price <= apiResponse.base.dbu_prices.dbu_current_price ? apiResponse.base.dbu_prices.dbu_current_price : apiResponse.base.dbu_prices.dbu_proposed_price;
    const finalTotal = apiResponse.base.final_prices.proposed_price <= apiResponse.base.final_prices.current_price ? apiResponse.base.final_prices.current_price : apiResponse.base.final_prices.proposed_price;
    const option = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },

        formatter: (params: any) => {
          // Try configurable tooltip first (if configured and not custom mode)
          if (this.data?.tooltip !== 'custom') {
            const tooltipCfg = this.data?.chartConfig?.tooltip;
            const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
            if (configurableFormatter) return (configurableFormatter as Function)(params);
          }

          // Custom tooltip case (chart inside tooltip)
          if (
            this.data?.tooltip === 'custom' &&
            params.value[0] === 'EBU + CES Imp'
          ) {
            setTimeout(() => {
              this.renderTooltipChart(params, apiResponse.popover); // reuse helper
            }, 0);

            return `
      <div id="tooltip-chart" style="width:250px;height:300px;"></div>
      <div style="font-size:12px;color:#555;"></div>
    `;
          }

          // Safety check
          if (params.componentType !== 'series' || !Array.isArray(params.value)) {
            return '';
          }

          // For the main waterfall bars (seriesIndex 0)
          if (params.seriesIndex === 0) {
            if (params.value.length < 3) {
              return '';
            }
            const diff = params.value[2] - params.value[1];
            return `<div style="text-align:left;"><b>${params.value[0]}</b><br/><b>Value:</b> ${oneDecimalWithCommas(diff)}</div>`;
          }

          // For top labels on 'DBU Cost' and 'DBU Out' (seriesIndex 1)
          if (params.seriesIndex === 1) {
            if (params.value.length < 4) {
              return '';
            }
            const value = params.value[3]; // Raw value from data
            return `<div style="text-align:left;"><b>${params.value[0]}</b><br/><b>Value:</b> ${oneDecimalWithCommas(value)}</div>`;
          }

          return '';
        },
      },
      dataset: {
        source: waterfallData,
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          fontSize: sc(10),
          hideOverlap: true,
          interval: 0,
          color: theme.chart?.axisLabelColor || theme.chart?.textColor || '#000',
          formatter: function (value: string) {
            return value.split(' ').join('\n');
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },

      yAxis: (() => {
        const yAxisCfg = this.data?.chartConfig?.yAxis;
        const configurableLabel = buildConfigurableAxisLabel(yAxisCfg);
        return {
          type: 'value',
          name: yAxisCfg?.name || 'ESP ($)',
          nameTextStyle: { fontSize: sc(11) },
          axisLabel: {
            fontSize: sc(10),
            hideOverlap: true,
            formatter: configurableLabel?.formatter || ((value: number) => formatStat(value)),
          },
          nameLocation: yAxisCfg?.nameLocation || 'center',
          nameGap: yAxisCfg?.nameGap || 40,
          splitLine: { show: true },
        };
      })(),
      grid: {
        // percentage strings so the global grid scanner's Math.max preserves them
        top: '18%',         // reserve room for Price Change badge
        right: '5%',
        left: '6%',
        bottom: '5%',
        containLabel: true,
      },
      series: [
        {
          type: 'custom',
          renderItem: (params: any, api: any) => {
            const barName = api.value(0);
            const barStart = api.value(1);
            const barEnd = api.value(2);
            const referenceType = api.value(3);

            const startCoord = api.coord([barName, barStart]);
            const endCoord = api.coord([barName, barEnd]);
            const barWidth = 40;
            let height = startCoord[1] - endCoord[1];
            height = Math.abs(height) < 3 && height !== 0 ? 3 : height;

            const style = api.style();
            if (referenceType === 1) {
              style.fill = '#8EC7FF'; // For stacked bars (e.g., proposed price on top)
            } else if (referenceType === 2) {
              style.fill = '#D3D3D3'; // For stacked bars (e.g., proposed price on top)
            } else if (barEnd - barStart < 0) {
              style.fill = '#F44336'; // red
            } else {
              style.fill = '#009B19'; // green
            }

            const shapes = [];

            // Main bar rectangle
            shapes.push({
              type: 'rect',
              shape: {
                x: endCoord[0] - barWidth / 2,
                y: endCoord[1],
                width: barWidth,
                height,
              },
              style,
            });

            return {
              type: 'group',
              children: shapes,
            };
          },

          encode: {
            x: 0,
            y: [1, 2],
          },
          datasetIndex: 0,
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => {
              const value = params.value[2] - params.value[1];
              return formatStatOneDecimal(value);
            },
            fontSize: sc(10),
            fontWeight: 'bold',
            color: theme.chart?.dataLabelInside || '#fff',
            textBorderColor: 'transparent',
            textBorderWidth: 0,
            textShadowBlur: 0,
            textShadowColor: 'transparent',
          },
        },
        {
          type: 'custom',
          renderItem: (function (outsideColor: string, scFn: (px: number) => number) {
            return function (params: any, api: any) {
              const categoryName = api.value(0);
              const value = api.value(1);
              const labelText = api.value(2);

              const coord = api.coord([categoryName, value]);

              return {
                type: 'text',
                style: {
                  text: labelText,
                  x: coord[0],
                  y: coord[1] - scFn(10),
                  textAlign: 'center',
                  textVerticalAlign: 'bottom',
                  fontSize: scFn(12),
                  fontWeight: 'bold',
                  fill: outsideColor,
                },
              };
            };
          })(theme.chart?.dataLabelOutside || theme.chart?.textColor || '#000', sc),
          encode: {
            x: 0,
            y: 1,
          },
          data: [
            [
              'DBU Cost',
              dbuTotal,
              formatStatOneDecimal(apiResponse.base.dbu_prices.dbu_proposed_price),
              apiResponse.base.dbu_prices.dbu_proposed_price
            ],
            [
              'DBU Out',
              finalTotal,
              formatStatOneDecimal(apiResponse.base.final_prices.proposed_price),
              apiResponse.base.final_prices.proposed_price
            ]
          ]

        }

      ],
      graphic: {
        elements: [
          {
            type: 'group',
            // Place at top-LEFT to avoid colliding with DBU Cost / DBU Out top labels (which sit at the right side of the plot)
            left: sc(60),
            top: sc(4),
            children: [
              {
                type: 'rect',
                shape: {
                  width: sc(100),
                  height: sc(42),
                  r: 8, // rounded corners
                },
                style: {
                  fill: '#eaf4ff',
                  stroke: '#999',
                  lineWidth: 1,
                  shadowBlur: 6,
                  shadowColor: 'rgba(0, 0, 0, 0.15)',
                  shadowOffsetX: 2,
                  shadowOffsetY: 2,
                },
              },
              {
                type: 'text',
                style: {
                  text: `Price Change\n${apiResponse.price_change.toFixed(1)}%`,
                  x: sc(50),
                  y: sc(25),
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  font: `bold ${sc(12)}px sans-serif`,
                  fill: '#333',
                },
              },
            ],
          },
        ],
      },
    };
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);
  }


  protected renderTooltipChart(params: any, popover: any) {
    const tooltipDom = document.getElementById('tooltip-chart');
    if (!tooltipDom) return;

    const tooltipChart = echarts.init(tooltipDom, 'dvm');
    const theme = this.themeService.resolve(this.data?.themeOverrides);

    const stackLabels = Object.keys(popover);
    const stackValues = Object.values(popover);

    const seriesData = stackValues.map((v, i) => ({
      type: 'bar',
      stack: 'total',
      name: stackLabels[i],
      barWidth: 50,
      data: [v],
      label: {
        show: true,
        position: 'inside',
        formatter: (val: any) => oneDecimalWithCommas(val.data),
        fontSize: 10,
        color: '#fff',
        fontWeight: 'bold',
        textBorderColor: 'transparent',
        textBorderWidth: 0,
        textShadowBlur: 0,
        textShadowColor: 'transparent',
      }
    }));

    tooltipChart.setOption({
      color: ['#00578A', '#424242', '#AB8B43', '#64AC97'],
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
      },
      legend: {

        data: stackLabels,
        top: 0,
        textStyle: { fontSize: 10 },
        orient: 'horizontal',
        itemGap: 10,
        // formatter: (name: string) => name.replace(/_/g, ' ').toUpperCase()
        formatter: (name: string) => {
          const customMap: Record<string, string> = {
            pid_change: "PID Change ",
            pba_ebu: "PBA EBU",
            pba_ces: "PBA CES",
            option_change: "Option Change"
          };
          return customMap[name] || name;
        }
      },
      xAxis: {
        type: 'category',
        data: [params.value[0]],
      },
      yAxis: { type: 'value', show: false },
      series: seriesData,
    });
  }


  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };
  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}

